import { createFileRoute, redirect } from "@tanstack/react-router";
import DatePicker from "@/assistance/datepicker";
import { getUserWithProfile, type CombinedUser } from "@/lib/collections/workerProfile-Users/getUserWithProfile";
import { useRealtimeCollection } from "@/hooks/realtime/useRealtimeCollection";
import { useMemo, useCallback, useState } from "react";
import { getUserSessionsAndBreaks } from "@/lib/collections/datapicker/getSessionsAndBreaks";

export const Route = createFileRoute('/_authenticated/assistance/$id/')({
  beforeLoad: ({ context }) => {
    if (!context.user) {
      throw redirect({ to: '/login' });
    }
    const { isRRHH, isAdmin } = context.userRoles;
    if (!isRRHH && !isAdmin) {
      throw redirect({ to: '/unauthorized' });
    }
  },

  loader: async ({ params }) => {
    const user = await getUserWithProfile(params.id);
    if (!user) throw new Response("Not Found", { status: 404 });
    return { user };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = Route.useLoaderData() as { user: CombinedUser };

  // Memoize these values to prevent re-creating on each render
  const dbId = useMemo(() => import.meta.env.VITE_APPWRITE_DATABASE_ID!, []);
  const collectionIds = useMemo(() => [
    import.meta.env.VITE_WORK_SESSIONS_COLLECTION_ID!,
    import.meta.env.VITE_WORK_BREAKS_COLLECTION_ID!,
    import.meta.env.VITE_WORK_SESSION_NOTES_COLLECTION_ID! // Agregamos las notas
  ], []);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const fetcher = useCallback(() => {
    if (!user.profile?.userId) {
      throw new Error('User profile not found');
    }
    const dateStr = selectedDate.toISOString().split('T')[0];
    return getUserSessionsAndBreaks(user.profile.userId, dateStr);
  }, [user.profile?.userId, selectedDate]);

  // Memoize filter events
  const filterEvents = useMemo(() => ['create', 'update', 'delete'], []);

  const [sessionsWithBreaks, _isLoading] = useRealtimeCollection(
    dbId,
    collectionIds,
    fetcher,
    filterEvents
  );

  return <DatePicker 
    user={user} 
    sessionsWithBreaks={sessionsWithBreaks}  
    onDateChange={setSelectedDate}   
    selectedDate={selectedDate} 
  />;
}